import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { EPath } from '@app/@core/enums/path.enum';
import { HttpErrorResponseEnum } from '@common/enums/http-error-response.enum';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '@shared/components/alert/alert.service';
import { HttpErrorSkipTypeEnum } from '@shared/enums/http-error-skip-type.enum';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';

@Injectable()
export class ServerErrorInterceptor implements HttpInterceptor {
  private alertService = inject(AlertService);
  private translateService = inject(TranslateService);
  private router = inject(Router);
  private dialogRef = inject(MatDialog);
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      tap({
        error: async (error: HttpErrorResponse) => {
          if (request.headers.has(HttpErrorSkipTypeEnum.skipErrorInterceptor)) return;
          switch (error.status) {
            case HttpStatusCode.Forbidden:
              if (request.headers.has(HttpErrorSkipTypeEnum.skipError403)) return;
              this.alertService.error(this.translateService.instant('alertMessages.noPermission'));
              break;
            case HttpStatusCode.Conflict:
              switch (error.error.message) {
                case HttpErrorResponseEnum.STRIPE_INACTIVE_CUSTOMER:
                  this.alertService.error(
                    this.translateService.instant('alertMessages.stripeInactiveCustomer'),
                  );
                  break;
                default:
                  if (request.headers.has(HttpErrorSkipTypeEnum.skipError409)) return;
                  this.alertService.error(
                    this.translateService.instant('alertMessages.conflictFound'),
                  );
              }
              break;
            case HttpStatusCode.NotFound:
              if (request.headers.has(HttpErrorSkipTypeEnum.skipError404)) return;
              await this.router.navigate([EPath.NotFound]);
              this.dialogRef.closeAll();
              break;
            case HttpStatusCode.InternalServerError:
              this.alertService.error(
                this.translateService.instant('alertMessages.internalServerError'),
              );
              break;
            case 0:
              this.alertService.error(
                this.translateService.instant('alertMessages.connectionError'),
                { autoClose: false },
              );
              break;
            case HttpStatusCode.BadGateway:
              this.alertService.error(
                this.translateService.instant('alertMessages.serverIsUnavailable'),
              );
              break;
            case HttpStatusCode.BadRequest: {
              const errorMessage = error?.error?.message ? ' ' + (error?.error?.message ?? '') : '';
              this.alertService.error(
                this.translateService.instant('alertMessages.badRequest') + errorMessage,
                { autoClose: false },
              );
              break;
            }
          }
        },
      }),
    );
  }
}
